/**
 * Zustand store for user and app state
 */

import { create } from 'zustand';
import type { User, Session, Message, NatalChart } from '../types';

interface UserState {
  // Auth state
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  primaryChart: NatalChart | null;

  // Session state
  sessions: Session[];
  currentSessionUuid: string | null;
  messages: Message[];
  isSessionLoading: boolean;
  hasMoreSessions: boolean;
  isLoadingMoreSessions: boolean;
  sessionsLoadedCount: number; // Track server-loaded count separately from sessions.length

  // Chat state
  isSending: boolean;
  isStreaming: boolean;
  toolStatus: string | null;

  // Actions
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setPrimaryChart: (chart: NatalChart | null) => void;
  setSessions: (sessions: Session[], hasMore?: boolean) => void;
  appendSessions: (sessions: Session[], hasMore: boolean) => void;
  setLoadingMoreSessions: (loading: boolean) => void;
  addSession: (session: Session) => void;
  setCurrentSession: (uuid: string | null) => void;
  setMessages: (messages: Message[]) => void;
  addMessage: (message: Message) => void;
  updateLastMessage: (content: string) => void;
  setSessionLoading: (loading: boolean) => void;
  setSending: (sending: boolean) => void;
  setStreaming: (streaming: boolean) => void;
  setToolStatus: (status: string | null) => void;
  updateSessionTitle: (uuid: string, title: string) => void;
  removeSession: (uuid: string) => void;
  moveSessionToTop: (uuid: string) => void;
  reset: () => void;
}

const initialState = {
  user: null,
  isLoading: true,
  isAuthenticated: false,
  primaryChart: null,
  sessions: [],
  currentSessionUuid: null,
  messages: [],
  isSessionLoading: false,
  hasMoreSessions: true,
  isLoadingMoreSessions: false,
  sessionsLoadedCount: 0,
  isSending: false,
  isStreaming: false,
  toolStatus: null,
};

export const useUserStore = create<UserState>((set) => ({
  ...initialState,

  setUser: (user) =>
    set({
      user,
      isAuthenticated: !!user,
      isLoading: false,
    }),

  setLoading: (isLoading) => set({ isLoading }),

  setPrimaryChart: (primaryChart) => set({ primaryChart }),

  setSessions: (sessions, hasMore = true) =>
    set({ sessions, hasMoreSessions: hasMore, sessionsLoadedCount: sessions.length }),

  appendSessions: (newSessions, hasMore) =>
    set((state) => {
      // Deduplicate: filter out any sessions that already exist
      const existingUuids = new Set(state.sessions.map((s) => s.session_uuid));
      const uniqueNewSessions = newSessions.filter(
        (s) => !existingUuids.has(s.session_uuid)
      );
      return {
        sessions: [...state.sessions, ...uniqueNewSessions],
        hasMoreSessions: hasMore,
        // Use uniqueNewSessions.length to avoid offset gaps when duplicates are filtered
        sessionsLoadedCount: state.sessionsLoadedCount + uniqueNewSessions.length,
      };
    }),

  setLoadingMoreSessions: (isLoadingMoreSessions) =>
    set({ isLoadingMoreSessions }),

  addSession: (session) =>
    set((state) => ({
      sessions: [session, ...state.sessions],
    })),

  setCurrentSession: (uuid) =>
    set({
      currentSessionUuid: uuid,
      messages: [],
    }),

  setMessages: (messages) => set({ messages }),

  addMessage: (message) =>
    set((state) => ({
      messages: [...state.messages, message],
    })),

  updateLastMessage: (content) =>
    set((state) => {
      const messages = [...state.messages];
      if (messages.length > 0) {
        const lastMessage = messages[messages.length - 1];
        if (lastMessage.role === 'assistant') {
          messages[messages.length - 1] = {
            ...lastMessage,
            content: lastMessage.content + content,
          };
        }
      }
      return { messages };
    }),

  setSessionLoading: (isSessionLoading) => set({ isSessionLoading }),

  setSending: (isSending) => set({ isSending }),

  setStreaming: (isStreaming) => set({ isStreaming }),

  setToolStatus: (toolStatus) => set({ toolStatus }),

  updateSessionTitle: (uuid, title) =>
    set((state) => ({
      sessions: state.sessions.map((s) =>
        s.session_uuid === uuid ? { ...s, title } : s
      ),
    })),

  removeSession: (uuid) =>
    set((state) => ({
      sessions: state.sessions.filter((s) => s.session_uuid !== uuid),
      currentSessionUuid:
        state.currentSessionUuid === uuid ? null : state.currentSessionUuid,
      messages: state.currentSessionUuid === uuid ? [] : state.messages,
    })),

  moveSessionToTop: (uuid) =>
    set((state) => {
      const session = state.sessions.find((s) => s.session_uuid === uuid);
      if (!session || state.sessions[0]?.session_uuid === uuid) {
        return state; // Already at top or not found
      }
      return {
        sessions: [
          { ...session, updated_at: new Date().toISOString() },
          ...state.sessions.filter((s) => s.session_uuid !== uuid),
        ],
      };
    }),

  reset: () => set(initialState),
}));

export default useUserStore;
